import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import {
  ClipboardList,
  Search,
  Check,
  X,
  Trash2,
  Calendar,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import BASE_URL from '../../url';

const TimesheetView = React.memo(({
  timesheets = [],
  loading = false,
  computedValues = {},
  timesheetHandlers = {},
  formatDate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Pending');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedApprover, setSelectedApprover] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch all companies for the company filter dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const response = await axios.get(`${BASE_URL}/api/companies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.success && Array.isArray(response.data.data)) {
          setCompanies(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching companies in TimesheetView:', error);
      }
    };
    fetchCompanies();
  }, []);

  // Merge companies found in timesheets with fetched companies to ensure all are listed
  const allCompanies = useMemo(() => {
    const map = new Map();
    companies.forEach(c => {
      if (c && c.id) map.set(String(c.id), c.name || c.Name || `Company ${c.id}`);
    });
    timesheets.forEach(ts => {
      const cId = ts.CompanyId || ts.EntityId || ts.companyId;
      const cName = ts.CompanyName || ts.companyName;
      if (cId && cName && !map.has(String(cId))) {
        map.set(String(cId), cName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [companies, timesheets]);

  // Unique approvers / supervisors from the current timesheets data
  const uniqueApprovers = useMemo(() => {
    const approvers = timesheets
      .map(ts => ts.PrimarySupervisorName || ts.primarySupervisorName || ts.ApproverName || ts.approverName)
      .filter(name => name && name.trim() !== '' && name !== '—' && name !== 'N/A');
    return [...new Set(approvers)].sort();
  }, [timesheets]);

  // Filter timesheets based on search and selected dropdown filters
  const filteredTimesheets = useMemo(() => {
    return timesheets.filter(ts => {
      const empName = (ts.employeeName || ts.EmployeeName || '').toLowerCase();
      const empCode = String(ts.EmployeeCode || ts.employeeCode || ts.EmployeeId || ts.employeeId || '').toLowerCase();
      const compName = (ts.CompanyName || ts.companyName || '').toLowerCase();
      const empEmail = (ts.EmployeeEmail || ts.employeeEmail || '').toLowerCase();
      const q = searchTerm.toLowerCase().trim();

      const matchesSearch = !q ||
        empName.includes(q) ||
        empCode.includes(q) ||
        compName.includes(q) ||
        empEmail.includes(q);

      const tsCompId = String(ts.CompanyId || ts.EntityId || ts.companyId || '');
      const matchesCompany = selectedCompany === 'all' || tsCompId === String(selectedCompany);

      const tsStatus = (ts.status || ts.Status || '').toLowerCase();
      const matchesStatus = filterStatus === 'all' || tsStatus === filterStatus.toLowerCase();

      const supName = ts.PrimarySupervisorName || ts.primarySupervisorName || ts.ApproverName || ts.approverName || '';
      const matchesApprover = selectedApprover === 'all' || supName === selectedApprover;

      return matchesSearch && matchesCompany && matchesStatus && matchesApprover;
    });
  }, [timesheets, searchTerm, selectedCompany, filterStatus, selectedApprover]);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCompany, filterStatus, selectedApprover]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredTimesheets.length / rowsPerPage) || 1;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredTimesheets.slice(indexOfFirstRow, indexOfLastRow);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Selected count among filtered items
  const selectedCount = useMemo(() => {
    return filteredTimesheets.filter(ts => ts.selected).length;
  }, [filteredTimesheets]);

  const allFilteredSelected = useMemo(() => {
    const selectable = filteredTimesheets.filter(ts => (ts.status || ts.Status) === 'Pending');
    return selectable.length > 0 && selectable.every(ts => ts.selected);
  }, [filteredTimesheets]);

  const handleToggleSelectAll = () => {
    if (typeof timesheetHandlers.handleSelectAllTimesheets === 'function') {
      timesheetHandlers.handleSelectAllTimesheets();
    }
  };

  const handleFormatDate = (dateVal) => {
    if (!dateVal) return 'N/A';
    if (typeof formatDate === 'function') return formatDate(dateVal);
    try {
      const d = new Date(dateVal);
      return d.toLocaleDateString();
    } catch {
      return String(dateVal);
    }
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#f9fafb', minHeight: '100vh', borderRadius: '12px' }}>
      {/* Header Container */}
      <div
        style={{
          marginBottom: '15px',
          background: '#ffffff',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
          <h1
            style={{
              fontSize: '22px',
              fontWeight: '700',
              color: '#111827',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <ClipboardList size={26} style={{ color: '#3b5bfd' }} />
            Timesheet Approvals
          </h1>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '0.88rem' }}>
            Review and approve pending timesheets across all companies
          </p>
        </div>

        {/* Filter Bar matching Image 4 */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search Box */}
          <div style={{ position: 'relative', flex: 1, minWidth: '240px' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }}
            />
            <input
              type="text"
              placeholder="Search by employee, ID or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                outline: 'none',
                fontSize: '0.88rem',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Company Filter Dropdown */}
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              outline: 'none',
              fontSize: '0.88rem',
              backgroundColor: '#ffffff',
              color: '#374151',
              minWidth: '170px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Companies</option>
            {allCompanies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {/* Status Filter Dropdown */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              outline: 'none',
              fontSize: '0.88rem',
              backgroundColor: '#ffffff',
              color: '#374151',
              minWidth: '140px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>

          {/* Approver Filter Dropdown */}
          <select
            value={selectedApprover}
            onChange={(e) => setSelectedApprover(e.target.value)}
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              outline: 'none',
              fontSize: '0.88rem',
              backgroundColor: '#ffffff',
              color: '#374151',
              minWidth: '160px',
              cursor: 'pointer'
            }}
          >
            <option value="all">All Approvers</option>
            {uniqueApprovers.map(approver => (
              <option key={approver} value={approver}>{approver}</option>
            ))}
          </select>

          {/* Bulk Approve Button */}
          <button
            type="button"
            onClick={timesheetHandlers.handleBulkApprove}
            disabled={!computedValues.hasSelectedTimesheets && selectedCount === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 18px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: (computedValues.hasSelectedTimesheets || selectedCount > 0) ? '#10b981' : '#e5e7eb',
              color: (computedValues.hasSelectedTimesheets || selectedCount > 0) ? '#ffffff' : '#9ca3af',
              fontWeight: 600,
              fontSize: '0.88rem',
              cursor: (computedValues.hasSelectedTimesheets || selectedCount > 0) ? 'pointer' : 'not-allowed',
              transition: 'background-color 0.2s',
              whiteSpace: 'nowrap'
            }}
          >
            <Check size={16} />
            Approve Selected ({computedValues.selectedCount || selectedCount})
          </button>

          {/* Optional Bulk Reject Button */}
          {timesheetHandlers.handleBulkReject && (
            <button
              type="button"
              onClick={timesheetHandlers.handleBulkReject}
              disabled={!computedValues.hasSelectedTimesheets && selectedCount === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 18px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: (computedValues.hasSelectedTimesheets || selectedCount > 0) ? '#ef4444' : '#f3f4f6',
                color: (computedValues.hasSelectedTimesheets || selectedCount > 0) ? '#ffffff' : '#9ca3af',
                fontWeight: 600,
                fontSize: '0.88rem',
                cursor: (computedValues.hasSelectedTimesheets || selectedCount > 0) ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              <X size={16} />
              Reject Selected ({computedValues.selectedCount || selectedCount})
            </button>
          )}
        </div>
      </div>

      {/* Main Table Card matching Image 4 */}
      <div
        style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '50px 1.6fr 1.6fr 1.2fr 1.4fr 0.8fr 1fr 1.1fr 1.1fr',
            padding: '14px 20px',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            fontWeight: 700,
            fontSize: '0.78rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#475569',
            gap: '12px',
            alignItems: 'center'
          }}
        >
          <div>
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={handleToggleSelectAll}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
          </div>
          <div>Employee and ID</div>
          <div>Email</div>
          <div>Phone</div>
          <div>Period</div>
          <div>Hours</div>
          <div>Status</div>
          <div>Approver</div>
          <div style={{ textAlign: 'center' }}>Action</div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div className="loading-spinner" style={{ margin: '0 auto', width: '36px', height: '36px' }}></div>
            <p style={{ marginTop: '14px', color: '#64748b', fontSize: '0.9rem' }}>Loading timesheets...</p>
          </div>
        ) : currentRows.length > 0 ? (
          <div>
            {currentRows.map(ts => {
              const tsId = ts.id || ts.Id;
              const isSelected = !!ts.selected;
              const isPending = (ts.status || ts.Status) === 'Pending';
              const isApproved = (ts.status || ts.Status) === 'Approved';
              const isRejected = (ts.status || ts.Status) === 'Rejected';

              const totalHours = Number(ts.totalHours || ts.TotalHours || 0);
              const overtimeHours = Number(ts.overtimeHours || ts.OvertimeHours || 0);

              const approverName = ts.ApproverName && ts.ApproverName !== '—' && ts.ApproverName !== 'N/A'
                ? ts.ApproverName
                : ts.PrimarySupervisorName && ts.PrimarySupervisorName !== '—'
                ? ts.PrimarySupervisorName
                : '—';

              return (
                <div
                  key={tsId}
                  onClick={() => {
                    if (typeof timesheetHandlers.handleViewTasks === 'function') {
                      timesheetHandlers.handleViewTasks(ts);
                    }
                  }}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '50px 1.6fr 1.6fr 1.2fr 1.4fr 0.8fr 1fr 1.1fr 1.1fr',
                    padding: '12px 20px',
                    borderBottom: '1px solid #f1f5f9',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                    gap: '12px',
                    backgroundColor: isSelected ? '#f0fdf4' : 'transparent'
                  }}
                  onMouseOver={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseOut={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {/* Checkbox */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => timesheetHandlers.handleSelectTimesheet(tsId)}
                      disabled={!isPending}
                      style={{ cursor: isPending ? 'pointer' : 'not-allowed', width: '16px', height: '16px' }}
                    />
                  </div>

                  {/* Employee & ID & Company */}
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        color: '#3b5bfd',
                        fontSize: '0.92rem',
                        lineHeight: 1.3
                      }}
                    >
                      {ts.employeeName || ts.EmployeeName || 'Unknown Employee'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                      ID: {ts.EmployeeCode || ts.employeeCode || ts.EmployeeId || ts.employeeId || 'No Code'}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#3b5bfd', fontWeight: 500, marginTop: '2px' }}>
                      {ts.CompanyName || ts.companyName || 'Prophecy Consulting INC'}
                    </div>
                  </div>

                  {/* Email */}
                  <div style={{ fontSize: '0.86rem', color: '#334155', wordBreak: 'break-word' }}>
                    {ts.employeeEmail || ts.EmployeeEmail || 'N/A'}
                  </div>

                  {/* Phone */}
                  <div style={{ fontSize: '0.86rem', color: '#334155' }}>
                    {ts.EmployeePhone || ts.employeePhone || 'N/A'}
                  </div>

                  {/* Period with Calendar icon */}
                  <div style={{ fontSize: '0.86rem', color: '#334155' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      <span>
                        {handleFormatDate(ts.periodStart || ts.PeriodStart)} – {handleFormatDate(ts.periodEnd || ts.PeriodEnd)}
                      </span>
                    </div>
                  </div>

                  {/* Hours & Overtime */}
                  <div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
                      {totalHours}h
                    </div>
                    {overtimeHours > 0 && (
                      <div style={{ fontSize: '0.74rem', color: '#ef4444', fontWeight: 600, marginTop: '1px' }}>
                        OT: {overtimeHours}h
                      </div>
                    )}
                  </div>

                  {/* Status Badge */}
                  <div>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '9999px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        backgroundColor: isApproved ? '#ecfdf5' : isRejected ? '#fef2f2' : '#fef3c7',
                        color: isApproved ? '#059669' : isRejected ? '#dc2626' : '#d97706',
                        border: `1px solid ${isApproved ? '#a7f3d0' : isRejected ? '#fecaca' : '#fde68a'}`
                      }}
                    >
                      {ts.status || ts.Status || 'Pending'}
                    </span>
                  </div>

                  {/* Approver */}
                  <div style={{ fontSize: '0.84rem' }}>
                    <span
                      style={{
                        color: isPending ? '#94a3b8' : '#334155',
                        fontStyle: isPending ? 'italic' : 'normal',
                        fontWeight: isPending ? 400 : 500
                      }}
                    >
                      {approverName}
                    </span>
                  </div>

                  {/* Actions matching Image 4 */}
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                  >
                    {isPending && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            timesheetHandlers.handleApproveTimesheet(tsId);
                          }}
                          style={{
                            padding: '6px',
                            borderRadius: '6px',
                            backgroundColor: '#ecfdf5',
                            color: '#10b981',
                            border: '1px solid #d1fae5',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.15s'
                          }}
                          title="Approve Timesheet"
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ecfdf5'}
                        >
                          <Check size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            timesheetHandlers.handleRejectTimesheet(tsId);
                          }}
                          style={{
                            padding: '6px',
                            borderRadius: '6px',
                            backgroundColor: '#fef2f2',
                            color: '#ef4444',
                            border: '1px solid #fee2e2',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.15s'
                          }}
                          title="Reject Timesheet"
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        >
                          <X size={15} />
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        timesheetHandlers.handleDeleteTimesheet(tsId);
                      }}
                      style={{
                        padding: '6px',
                        borderRadius: '6px',
                        backgroundColor: '#fff1f2',
                        color: '#e11d48',
                        border: '1px solid #fecdd3',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.15s'
                      }}
                      title="Delete Timesheet"
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#ffe4e6'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff1f2'}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#94a3b8' }}>
            <ClipboardList size={42} style={{ margin: '0 auto 12px auto', opacity: 0.6 }} />
            <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 500, color: '#64748b' }}>
              No timesheets found matching the current filters.
            </p>
          </div>
        )}

        {/* Pagination Bar */}
        {filteredTimesheets.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 20px',
              backgroundColor: '#ffffff',
              borderTop: '1px solid #f1f5f9',
              flexWrap: 'wrap',
              gap: '12px'
            }}
          >
            {/* Left: Rows Per Page */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.84rem' }}>
              <span>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '5px 10px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#ffffff',
                  fontSize: '0.84rem',
                  color: '#374151',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span style={{ marginLeft: '8px', color: '#64748b' }}>
                Showing {filteredTimesheets.length > 0 ? indexOfFirstRow + 1 : 0}–{Math.min(indexOfLastRow, filteredTimesheets.length)} of {filteredTimesheets.length}
              </span>
            </div>

            {/* Right: Pagination Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: currentPage === 1 ? '#f3f4f6' : '#ffffff',
                  color: currentPage === 1 ? '#9ca3af' : '#374151',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '0.84rem'
                }}
              >
                <ChevronLeft size={16} />
                Previous
              </button>

              <div style={{ fontSize: '0.84rem', color: '#64748b' }}>
                Page {currentPage} of {totalPages}
              </div>

              <button
                type="button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#ffffff',
                  color: currentPage === totalPages ? '#9ca3af' : '#374151',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '0.84rem'
                }}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

TimesheetView.displayName = 'TimesheetView';

export default TimesheetView;